import {
  IconChevronDown,
  IconChevronUp,
  IconSelector,
} from '@tabler/icons-react'
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_Icons,
  type MRT_TableOptions,
  useMantineReactTable,
} from 'mantine-react-table'
import { type JSX, useMemo } from 'react'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { TABLE_DEFAULT_PAGE_SIZE } from '#src/constants/tables.ts'
import { createReactTableUrlContext } from '#src/stores/reactTableUrl.ts'

type ReactTableProps<T extends object> = {
  columnConfigs: ColumnConfig<T>[]
  data: T[]
  tableOptions?: Omit<MRT_TableOptions<T>, 'columns' | 'data'>
  /** Optional name for the table to persist state in URL. If not provided, state won't be persisted */
  tableName?: string
}

function TableContent<T extends object>({
  columnConfigs,
  data,
  tableOptions,
  useTableStore,
}: {
  columnConfigs: ColumnConfig<T>[]
  data: T[]
  tableOptions?: Omit<MRT_TableOptions<T>, 'columns' | 'data'>
  useTableStore: ReturnType<
    typeof createReactTableUrlContext
  >['useContextStore']
}): JSX.Element {
  const {
    columnFilters,
    setColumnFilters,
    showColumnFilters,
    setShowColumnFilters,
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
  } = useTableStore((state) => state)

  const columns = useMemo(
    () =>
      columnConfigs.map((config): MRT_ColumnDef<T, T> => ({
        accessorKey: config.id,
        header: config.headerText,
        Cell: (context) => config.render(context.row.original),
        accessorFn: config.getSortFilterValue,
        filterVariant: config.filteringType,
        enableSorting: config.enableSorting,
        enableColumnFilter: config.filteringType != null,
        enableGlobalFilter: config.filteringType != null,
        sortDescFirst: config.sortDescFirst,
        mantineFilterMultiSelectProps:
          config.filteringType === 'multi-select'
            ? { data: config.options }
            : undefined,
        size: config.size,
        minSize: config.minSize,
        maxSize: config.maxSize,
      })),
    [columnConfigs]
  )

  const enablePagination = data.length > TABLE_DEFAULT_PAGE_SIZE
  const table = useMantineReactTable<T>({
    data,
    columns,
    icons,
    enableStickyHeader: true,
    enableStickyFooter: true,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: true,
      radius: 'md',
      className: 'h-full flex flex-col print:h-auto! print:block!',
    },
    mantineTableContainerProps: {
      // `enableStickyHeader` sets an inline max-height + overflow:auto on the
      // scroll container, which clamps the printed output to ~one viewport.
      // Release both for print so all rows flow across pages.
      className: 'flex-1 print:max-h-none! print:overflow-visible!',
    },
    layoutMode: 'grid',
    mantineTableHeadCellProps: {
      className: 'bg-muted font-medium first:rounded-tl-md last:rounded-tr-md',
    },
    mantineBottomToolbarProps: {
      className: 'print:hidden',
    },

    mantineTableBodyCellProps: {
      className:
        'wrap-break-word whitespace-normal flex flex-col items-start justify-center',
    },

    // hide all buttons in the top toolbar
    enableTopToolbar: false,
    enableFullScreenToggle: false,
    enableDensityToggle: false,
    enableSortingRemoval: false,

    // TODO enable this again when @tanstack/table global filtering with null in column is fixed
    // see https://github.com/TanStack/table/issues/4919
    enableGlobalFilter: false,

    enablePagination,
    enableBottomToolbar: enablePagination,

    state: {
      columnFilters,
      showColumnFilters,
      sorting,
      globalFilter,
      pagination: { pageIndex, pageSize },
      density: 'xs',
    },

    onColumnFiltersChange: setColumnFilters,
    onShowColumnFiltersChange: setShowColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({ pageIndex, pageSize })
        setPageIndex(newPagination.pageIndex)
        setPageSize(newPagination.pageSize)
      } else {
        setPageIndex(updater.pageIndex)
        setPageSize(updater.pageSize)
      }
    },

    renderEmptyRowsFallback: () => (
      <div className="flex items-center justify-center bg-gray-100">
        <div className="text-gray-500">No results found</div>
      </div>
    ),

    positionActionsColumn: 'last',

    columnFilterDisplayMode: 'popover',

    ...tableOptions,
  })

  return <MantineReactTable table={table} />
}

export function ReactTable<T extends object>({
  columnConfigs,
  data,
  tableOptions,
  tableName,
}: ReactTableProps<T>): JSX.Element {
  const initialSort = useMemo(() => {
    const initialSortColumn =
      columnConfigs.find((config) => config.useAsInitialSort != null) ??
      columnConfigs.find((config) => config.enableSorting)
    if (initialSortColumn != null) {
      return [
        {
          id: initialSortColumn.id,
          desc: initialSortColumn.sortDescFirst ?? false,
        },
      ]
    }

    return []
  }, [columnConfigs])

  // If tableName is provided, create a context for this specific table
  const { Provider: TableProvider, useContextStore: useTableStore } = useMemo(
    () =>
      createReactTableUrlContext({
        name: tableName ?? '_table',
        defaultSort: initialSort,
      }),
    [tableName, initialSort]
  )

  return (
    <TableProvider>
      <TableContent
        columnConfigs={columnConfigs}
        data={data}
        tableOptions={tableOptions}
        useTableStore={useTableStore}
      />
    </TableProvider>
  )
}

const icons: Partial<MRT_Icons> = {
  //change sort icon, connect internal props so that it gets styled correctly
  IconSortDescending: IconChevronDown,
  IconSortAscending: IconChevronUp,
  IconArrowsSort: IconSelector,
}
